import { motion } from "framer-motion";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const sections = [
  {
    title: "Overview",
    content:
      "This Privacy Policy explains how Mosheli Creative Solutions LLC (\"Mosheli\", \"we\", \"us\") collects, uses, and protects information when you visit our website or engage with our services. By using our site, you agree to the practices described below.",
  },
  {
    title: "Information We Collect",
    content:
      "We collect information you choose to share with us, such as your name, email address, company name, and project details when you submit a form, schedule a call, or communicate with our team. We also collect limited technical data like browser type, device information, and pages visited to help us improve site performance and user experience.",
  },
  {
    title: "How We Use Your Information",
    content:
      "We use your information to respond to inquiries, provide proposals, deliver services, and maintain ongoing client relationships. We may also use aggregated analytics to understand website usage, improve content, and optimize marketing performance. We never sell your personal information.",
  },
  {
    title: "Sharing and Disclosure",
    content:
      "We share information only with trusted service providers who support our operations, such as analytics, scheduling, or email services. These providers are authorized to use your information solely for the purpose of delivering services on our behalf. We may disclose information if required by law or to protect our rights and safety.",
  },
  {
    title: "Cookies and Tracking Technologies",
    content:
      "We use cookies and similar technologies to remember preferences, analyze traffic, and understand how visitors interact with our site. You can adjust your browser settings to decline cookies, but some site features may not function as intended. For more details, review our Cookie Policy.",
  },
  {
    title: "Data Security",
    content:
      "We take reasonable administrative and technical measures to protect your data, including secure hosting, encryption in transit, and access controls. No online transmission is 100% secure, but we continually review and improve our safeguards.",
  },
  {
    title: "Data Retention",
    content:
      "We retain information only as long as necessary to fulfill business or legal obligations. If you request deletion, we will remove your data unless we are required to keep it for legal or contractual reasons.",
  },
  {
    title: "Your Rights",
    content:
      "You may request access to, correction of, or deletion of your personal information. To make a request, contact us at create@mosheli.com. We will respond within a reasonable timeframe.",
  },
  {
    title: "Third-Party Links",
    content:
      "Our website may include links to third-party sites. We are not responsible for their privacy practices, and we encourage you to review their policies before sharing information.",
  },
  {
    title: "Contact Us",
    content:
      "If you have questions about this Privacy Policy or how we handle data, email us at create@mosheli.com or use the contact form on our website.",
  },
];

export default function PrivacyPolicy() {
  return (
    <>
      <Header />
      <div className="min-h-screen bg-background">
        <section className="relative pt-32 pb-20 px-4 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-secondary/20 to-background" />
          <div className="container relative z-10 max-w-4xl mx-auto text-center">
            <motion.h1
              className="text-5xl md:text-6xl font-bold mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              Privacy <span className="text-gradient">Policy</span>
            </motion.h1>
            <motion.p
              className="text-xl text-muted-foreground mb-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              Effective date: January 25, 2026
            </motion.p>
            <motion.p
              className="text-base text-muted-foreground"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              We respect your privacy and are committed to protecting the information you share with us.
            </motion.p>
          </div>
        </section>

        <section className="py-20 px-4">
          <div className="container max-w-4xl mx-auto space-y-10">
            {sections.map((section, index) => (
              <motion.div
                key={section.title}
                className="p-6 md:p-8 rounded-2xl bg-card border border-border"
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
              >
                <h2 className="text-2xl font-semibold mb-3">{section.title}</h2>
                <p className="text-muted-foreground leading-relaxed">{section.content}</p>
              </motion.div>
            ))}
          </div>
        </section>
      </div>
      <Footer />
    </>
  );
}